import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Search, UserPlus, UserCheck, UserMinus } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface UserSearchProps {
  currentUserId: string;
}

const ITEMS_PER_PAGE = 20;

const UserSearch = ({
 currentUserId }: UserSearchProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Helper function for avatar URL - handles URLs that may already have cache buster
  const getAvatarUrl = (avatarUrl: string | null | undefined): string | undefined => {
    if (!avatarUrl) return undefined;
    // If URL already has a timestamp, use it as-is
    if (avatarUrl.includes('?t=') || avatarUrl.includes('&t=')) {
      return avatarUrl;
    }
    const separator = avatarUrl.includes('?') ? '&' : '?';
    return `${avatarUrl}${separator}t=${Date.now()}`;
  };

  // Fetch total user count (excluding deleted users)
  const { data: totalUserCount = 0 } = useQuery({
    queryKey: ["total-user-count"],
    queryFn: async () => {
      // Get all profiles with usernames
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select("id")
        .not("username", "is", null);
      
      if (error) throw error;
      if (!profiles || profiles.length === 0) return 0;
      
      // Filter out soft-deleted users
      const userIds = profiles.map(p => p.id);
      const { data: activeUserIds } = await (supabase as any).rpc('get_active_user_ids', {
        user_ids: userIds
      });
      
      return (activeUserIds as string[])?.length || 0;
    },
  });

  // Fetch blocked users
  const { data: blockedUserIds = [] } = useQuery({
    queryKey: ["blocked-users", currentUserId],
    queryFn: async () => {
      const { data: usersIBlocked, error: error1 } = await supabase
        .from("blocked_users")
        .select("blocked_user_id")
        .eq("user_id", currentUserId);
      
      const { data: usersWhoBlockedMe, error: error2 } = await supabase
        .from("blocked_users")
        .select("user_id")
        .eq("blocked_user_id", currentUserId);
      
      if (error1 || error2) throw error1 || error2;
      
      const blockedByMe = usersIBlocked?.map(b => b.blocked_user_id) || [];
      const blockedMe = usersWhoBlockedMe?.map(b => b.user_id) || [];
      return [...new Set([...blockedByMe, ...blockedMe])];
    },
  });

  // Search users with pagination
  const { data: searchResults = [], isLoading: searching } = useQuery({
    queryKey: ["user-search", searchQuery, blockedUserIds],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];

      const { data, error } = await supabase
        .from("profiles")
        .select("id, username, avatar_url, is_public")
        .or(`username.ilike.%${searchQuery}%`)
        .not("username", "is", null)
        .neq("id", currentUserId);
      if (error) throw error;

      if (!data || data.length === 0) return [];

      // Filter out blocked users and deleted accounts
      // Check if each user's auth account still exists (not soft-deleted)
      const userIds = data.map((u: any) => u.id);
      
      // Query auth.users to check which accounts are deleted
      const { data: activeUserIdsArray, error: rpcError } = await (supabase as any).rpc('get_active_user_ids', {
        user_ids: userIds
      });

      if (rpcError) {
        // Fallback: just filter blocked users
        return data.filter((user: any) => !blockedUserIds.includes(user.id));
      }

      const activeUserIds = new Set((activeUserIdsArray as string[]) || []);

      const filtered = data.filter(
        (user: any) => !blockedUserIds.includes(user.id) && activeUserIds.has(user.id)
      );

      return filtered;
    },
    enabled: searchQuery.length >= 2,
  });

  // Reset to first page when search query changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0);
  };

  const paginatedResults = searchResults.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(searchResults.length / ITEMS_PER_PAGE);

  // Query for follow statuses of search results
  const { data: followStatuses = {} } = useQuery({
    queryKey: ["follow-statuses-bulk", searchResults.map((u: any) => u.id), currentUserId],
    queryFn: async () => {
      if (searchResults.length === 0) return {};

      const userIds = searchResults.map((u: any) => u.id);

      // Get all follows
      const { data: followsData } = await supabase
        .from("follows")
        .select("following_id")
        .eq("follower_id", currentUserId)
        .in("following_id", userIds);

      // Get all pending requests
      const { data: requestsData } = await supabase
        .from("follow_requests")
        .select("target_user_id")
        .eq("requester_id", currentUserId)
        .eq("status", "pending")
        .in("target_user_id", userIds);

      const statuses: Record<string, { isFollowing: boolean; hasRequested: boolean }> = {};
      
      userIds.forEach(id => {
        statuses[id] = {
          isFollowing: followsData?.some(f => f.following_id === id) || false,
          hasRequested: requestsData?.some(r => r.target_user_id === id) || false,
        };
      });

      return statuses;
    },
    enabled: searchResults.length > 0,
  });

  // Mutation to follow a user
  const followUser = useMutation({
    mutationFn: async (targetUserId: string) => {
      const { data, error } = await supabase.rpc("follow_user", {
        p_target_user_id: targetUserId,
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; status?: string } | null;
      if (!result?.success) throw new Error(result?.error || "Failed to follow user");
      
      return { targetUserId, status: result.status };
    },
    onSuccess: async ({ targetUserId, status }) => {
      queryClient.invalidateQueries({ queryKey: ["follow-statuses-bulk"] });
      queryClient.invalidateQueries({ queryKey: ["follow-status", currentUserId, targetUserId] });
      
      if (status === "following") {
        toast.success(t('notifications.toast.following'));
        
        // Send push notification for new follow on public profile
        try {
          await supabase.functions.invoke('send-follow-notification', {
            body: {
              type: 'new_follow',
              targetUserId: targetUserId,
              requesterId: currentUserId,
            }
          });
        } catch (error) {
          console.error('Failed to send notification:', error);
        }
      } else if (status === "pending" || status === "requested") {
        toast.success(t('notifications.toast.follow_request_sent'));
        
        // Send push notification for follow request
        try {
          console.log('Sending follow request notification...', {
            type: 'follow_request',
            targetUserId: targetUserId,
            requesterId: currentUserId,
          });
          const result = await supabase.functions.invoke('send-follow-notification', {
            body: {
              type: 'follow_request',
              targetUserId: targetUserId,
              requesterId: currentUserId,
            }
          });
          console.log('Follow request notification result:', result);
        } catch (error) {
          console.error('Failed to send notification:', error);
        }
      }
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to follow user");
    },
  });

  // Mutation to unfollow a user
  const unfollowUser = useMutation({
    mutationFn: async (targetUserId: string) => {
      const { data, error } = await supabase.rpc("unfollow_user", {
        p_target_user_id: targetUserId,
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string } | null;
      if (!result?.success) throw new Error(result?.error || "Failed to unfollow user");
      
      return targetUserId;
    },
    onSuccess: (targetUserId) => {
      queryClient.invalidateQueries({ queryKey: ["follow-statuses-bulk"] });
      queryClient.invalidateQueries({ queryKey: ["follow-status", currentUserId, targetUserId] });
      toast.success("Unfollowed");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to unfollow");
    },
  });

  // Mutation to cancel follow request
  const cancelRequest = useMutation({
    mutationFn: async (targetUserId: string) => {
      const { data, error } = await supabase.rpc("cancel_follow_request", {
        p_target_user_id: targetUserId,
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string } | null;
      if (!result?.success) throw new Error(result?.error || "Failed to cancel request");
      
      return targetUserId;
    },
    onSuccess: (targetUserId) => {
      queryClient.invalidateQueries({ queryKey: ["follow-statuses-bulk"] });
      queryClient.invalidateQueries({ queryKey: ["follow-status", currentUserId, targetUserId] });
      toast.success(t('notifications.toast.follow_request_cancelled'));
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to cancel request");
    },
  });

  const renderFollowButton = (user: any) => {
    const status = followStatuses[user.id] || { isFollowing: false, hasRequested: false };

    if (status.isFollowing) {
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={() => unfollowUser.mutate(user.id)}
          disabled={unfollowUser.isPending}
        >
          <UserCheck className="w-3 h-3 mr-1" />
          {t('ui.user_search.following')}
        </Button>
      );
    } else if (status.hasRequested) {
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={() => cancelRequest.mutate(user.id)}
          disabled={cancelRequest.isPending}
        >
          <UserMinus className="w-3 h-3 mr-1" />
          {t('ui.user_search.requested')}
        </Button>
      );
    } else {
      return (
        <Button
          size="sm"
          onClick={() => followUser.mutate(user.id)}
          disabled={followUser.isPending}
        >
          <UserPlus className="w-3 h-3 mr-1" />
          {t('ui.user_search.follow')}
        </Button>
      );
    }
  };

  return (
    <Card className="p-4 mb-6">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder={t('social.user_search.search_all') + ` ${totalUserCount.toLocaleString()} ` + t('social.user_search.users_placeholder')}
          value={searchQuery}
          onChange={handleSearchChange}
          className="pl-10"
        />
      </div>

      {searchQuery.length >= 2 && (
        <div className="mt-3">
          <div className="space-y-2">
            {searching ? (
              <p className="text-sm text-muted-foreground text-center py-4">{t('ui.user_search.searching')}</p>
            ) : searchResults.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">{t('ui.user_search.no_users')}</p>
            ) : (
              <>
                {paginatedResults.map((user: any) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <Avatar 
                      className="h-10 w-10 cursor-pointer"
                      onClick={() => navigate(`/${user.username}`)}
                    >
                      <AvatarImage 
                        src={getAvatarUrl(user.avatar_url)}
                        alt={user.username || 'User'}
                        className="object-cover"
                        loading="lazy"
                      />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {user.username?.[0] || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => navigate(`/${user.username}`)}
                    >
                      <p className="font-medium truncate">
                        {user.username}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        @{user.username}
                      </p>
                    </div>
                    {renderFollowButton(user)}
                  </div>
                ))}
              </>
            )}
          </div>
          
          {totalPages > 1 && searchResults.length > 0 && (
            <div className="flex items-center justify-between border-t pt-4 mt-4">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
              >
                {t('social.user_search.previous')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {t('social.user_search.page')} {currentPage + 1} {t('social.user_search.of')} {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage >= totalPages - 1}
              >
                {t('social.user_search.next')}
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default UserSearch;
